import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FilterGroupItem } from '../utils/filterSettings';
import { DEFAULT_PAGE_SIZE } from '../utils/tableSettings';
import type { TableStateControl } from './useTableStateControl.types';
import { createEmptyFilterGroup } from './useTableStateControl.utils';

type PersistedFilterState = {
  filters?: FilterGroupItem;
  globalFilter?: string;
  sorting?: SortingState;
};

const FILTER_STATE_PREFIX = 'table-filter-state-';

function loadFilterState(storageKey: string): PersistedFilterState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(`${FILTER_STATE_PREFIX}${storageKey}`);
    if (!stored) return null;
    return JSON.parse(stored) as PersistedFilterState;
  } catch {
    return null;
  }
}

function saveFilterState(storageKey: string, state: PersistedFilterState): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(`${FILTER_STATE_PREFIX}${storageKey}`, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

/**
 * Options for useTableStoreState hook
 */
export type UseTableStoreStateOptions<_TData = unknown> = {
  /**
   * Initial filters (optional)
   */
  initialFilters?: FilterGroupItem;
  /**
   * Initial global filter (optional)
   */
  initialGlobalFilter?: string;
  /**
   * Initial sorting state (optional)
   */
  initialSorting?: SortingState;
  /**
   * Initial pagination state (optional)
   */
  initialPagination?: PaginationState;
  /**
   * Default page size (default: 50)
   */
  defaultPageSize?: number;
  /**
   * Optional key for persisting filters
   */
  storageKey?: string;
};

/**
 * Hook for managing table state via React state (no URL synchronization)
 * Use this when you don't want URL-driven state management
 * All options are optional - can be called with no props
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const tableState = useTableStoreState({
 *     defaultPageSize: 50,
 *     totalRows: 1000,
 *   });
 *   // ...
 * }
 * ```
 *
 * @example In ServerTable
 * ```tsx
 * function ServerTable() {
 *   const tableState = useTableStoreState({ totalRows: rowCount });
 *   // Use tableState with your table...
 * }
 * ```
 */
export function useTableStoreState<TData = unknown>({
  initialFilters,
  initialGlobalFilter = '',
  initialSorting = [],
  initialPagination,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  storageKey,
}: UseTableStoreStateOptions<TData> = {}): TableStateControl {
  const [filters, setFiltersState] = useState<FilterGroupItem>(() => {
    if (storageKey) {
      const saved = loadFilterState(storageKey);
      if (saved?.filters) return saved.filters;
    }
    return initialFilters || createEmptyFilterGroup();
  });
  const [globalFilterState, setGlobalFilterState] = useState<string>(() => {
    if (storageKey) {
      const saved = loadFilterState(storageKey);
      if (saved?.globalFilter !== undefined) return saved.globalFilter;
    }
    return initialGlobalFilter;
  });
  const [sortingState, setSortingState] = useState<SortingState>(() => {
    if (storageKey) {
      const saved = loadFilterState(storageKey);
      if (saved?.sorting) return saved.sorting;
    }
    return initialSorting;
  });
  const [paginationState, setPaginationState] = useState<PaginationState>(
    initialPagination || {
      pageIndex: 0,
      pageSize: defaultPageSize,
    },
  );

  useEffect(() => {
    if (!storageKey) return;
    saveFilterState(storageKey, { filters, globalFilter: globalFilterState, sorting: sortingState });
  }, [storageKey, filters, globalFilterState, sortingState]);

  // Handle global filter change - reset pagination to first page
  const handleGlobalFilterChange = useCallback((value: string) => {
    setGlobalFilterState(value);
    // Reset to first page when global filter changes
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Wrapper for sorting that handles OnChangeFn signature
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updaterOrValue) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sortingState) : updaterOrValue;
      setSortingState(newSorting);
    },
    [sortingState],
  );

  // Wrapper for pagination that handles OnChangeFn signature
  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updaterOrValue) => {
      const newPagination = typeof updaterOrValue === 'function' ? updaterOrValue(paginationState) : updaterOrValue;
      setPaginationState(newPagination);
    },
    [paginationState],
  );

  // Handle filters change - reset pagination to first page
  const handleFiltersChange = useCallback((newFilters: FilterGroupItem) => {
    setFiltersState(newFilters);
    // Reset to first page when filters change
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Helper functions
  const currentPage = paginationState.pageIndex + 1;

  const setCurrentPage = useCallback((page: number) => {
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: page - 1,
    }));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPaginationState((prev) => ({
      ...prev,
      pageSize: size,
      pageIndex: 0, // Reset to first page
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(createEmptyFilterGroup());
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const defaultFilters = useMemo(() => initialFilters ?? createEmptyFilterGroup(), [initialFilters]);

  const resetToDefaults = useCallback(() => {
    setFiltersState(defaultFilters);
    setGlobalFilterState('');
    setSortingState(initialSorting);
    setPaginationState({ pageIndex: 0, pageSize: defaultPageSize });
  }, [defaultFilters, initialSorting, defaultPageSize]);

  return {
    filters,
    setFilters: handleFiltersChange,
    globalFilter: globalFilterState,
    setGlobalFilter: handleGlobalFilterChange,
    sorting: sortingState,
    setSorting: handleSortingChange,
    pagination: paginationState,
    setPagination: handlePaginationChange,
    currentPage,
    setCurrentPage,
    setPageSize,
    defaultPageSize,
    defaultSorting: initialSorting,
    defaultFilters,
    resetFilters,
    resetToDefaults,
  };
}
